import * as localStorageKeys from "../../constants/localStorageKeys";

import { DATE_FORMAT, TABLE_ROWS_OPTIONS } from "../../constants";
import { ILanguage, LangContext } from "../../context/LangContext";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  makeStyles,
} from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { formatSsn, getScreeningStatusText } from "../../utils";

import { CancerScreeningResultModel } from "../../models/cancerScreeningResultModel";
import NoResults from "../NoResults";
import moment from "moment";
import {oldNorseSort} from "old-norse-alphabet-sort";
import translations from "../../assets/json/translations.json";
import { useHistory } from "react-router-dom";

interface IProps {
  screeningRequests: CancerScreeningResultModel[];
  currentPage: number;
  rowsPerPage: number;
  count: number;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    "& th": {
      fontWeight: 600,
      fontSize: 15,
    },
    "& td": {
      color: theme.palette.text.secondary,
      fontSize: 15,
    },
  },
}));

let lang: ILanguage;

const ScreeningRequestsOverviewList: React.FC<IProps> = ({
  screeningRequests,
  currentPage,
  rowsPerPage,
  count,
  setCurrentPage,
  setRowsPerPage,
}) => {
  const classes = useStyles({});
  [lang] = useContext(LangContext);
  const history = useHistory();

  const [sortKey, setSortKey] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedScreeningRequests, setSortedScreeningRequests] = useState<
    CancerScreeningResultModel[]
  >([]);
  const startIndex = currentPage * rowsPerPage;

  useEffect(() => {
    const localStorageSortDirection = localStorage.getItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_SORT_DIRECTION}`
    );
    const localStorageSortKey = localStorage.getItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_SORT_KEY}`
    );
    if (localStorageSortDirection) {
      setSortDirection(localStorageSortDirection as "asc" | "desc");
    }
    if (localStorageSortKey) {
      setSortKey(localStorageSortKey);
    }
  }, []);

  useEffect(() => {
    if (sortKey && tableColumns[sortKey]) {
      const sorted = tableColumns[sortKey].sort
        ? [...screeningRequests].sort(tableColumns[sortKey].sort)
        : [...screeningRequests];
      setSortedScreeningRequests(
        sortDirection === "asc" ? sorted : sorted.reverse()
      );
    } else {
      setSortedScreeningRequests(screeningRequests);
    }
  }, [screeningRequests, sortKey, sortDirection]);

  return (
    <Paper elevation={2} className={classes.container}>
      {screeningRequests.length ? (
        <React.Fragment>
          <TableContainer>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  {Object.keys(tableColumns).map((columnKey) => (
                    <TableCell key={columnKey}>
                      {tableColumns[columnKey].sort ? (
                        <TableSortLabel
                          active={sortKey === columnKey}
                          direction={sortDirection}
                          onClick={() => onSortClick(columnKey)}
                        >
                          {tableColumns[columnKey].translation[lang]}
                        </TableSortLabel>
                      ) : (
                        tableColumns[columnKey].translation[lang]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedScreeningRequests
                  .slice(startIndex, startIndex + rowsPerPage)
                  .map((screeningRequest, index) => (
                    <TableRow
                      hover
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() => clickOnRequest(screeningRequest)}
                    >
                      <TableCell>{screeningRequest.personName}</TableCell>
                      <TableCell>
                        {formatSsn(screeningRequest.personSsn)}
                      </TableCell>
                      <TableCell>
                        {screeningRequest.examinationDateTime &&
                          moment(screeningRequest.examinationDateTime).format(
                            DATE_FORMAT
                          )}
                      </TableCell>
                      <TableCell>
                        {screeningRequest.examinationReasonDescription}
                      </TableCell>
                      <TableCell>
                        {screeningRequest.createDateTime &&
                          moment(screeningRequest.createDateTime).format(
                            DATE_FORMAT
                          )}
                      </TableCell>
                      <TableCell>{screeningRequest.requestorName}</TableCell>
                      <TableCell>
                        {screeningRequest.examinationNotes && "Já"}
                      </TableCell>
                      <TableCell>
                        {getScreeningStatusText(screeningRequest.screeningStatus ?? -1, lang)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            labelRowsPerPage={translations.numberOfRowsPerPage[lang]}
            rowsPerPageOptions={TABLE_ROWS_OPTIONS}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onChangePage={(_, p) => setCurrentPage(p)}
            onChangeRowsPerPage={(e) => {
              setCurrentPage(0);
              setRowsPerPage(parseInt(e.target.value, 10));
            }}
          />
        </React.Fragment>
      ) : (
        <NoResults message={translations.noScreeningRequestsFound[lang]} />
      )}
    </Paper>
  );

  function onSortClick(columnKey: string) {
    if (sortKey === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(columnKey);
      setSortDirection("asc");
    }
  }

  function clickOnRequest(screeningRequest: CancerScreeningResultModel) {
    localStorage.setItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_SORT_DIRECTION}`,
      sortDirection
    );
    localStorage.setItem(
      `${localStorageKeys.NEW_SCREENING_REQUESTS_SORT_KEY}`,
      sortKey
    );
    history.push(`/screening-request-overview/${screeningRequest.id}`);
  }
};

const tableColumns: {
  [key: string]: {
    translation: { is: string; en: string };
    sort?: (
      a: CancerScreeningResultModel,
      b: CancerScreeningResultModel
    ) => number;
  };
} = {
  name: {
    translation: translations.name,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.personName) {
        return -1;
      }
      if (!b.personName) {
        return 1;
      }
      return oldNorseSort(a.personName, b.personName);
    },
  },
  ssn: {
    translation: translations.kennitala,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.personSsn) {
        return -1;
      }
      if (!b.personSsn) {
        return 1;
      }
      return a.personSsn.localeCompare(b.personSsn) < 0 ? 1 : -1;
    },
  },
  examinationDate: {
    translation: translations.examinationDate,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.examinationDateTime) {
        return -1;
      }
      if (!b.examinationDateTime) {
        return 1;
      }
      return moment(a.examinationDateTime).isAfter(b.examinationDateTime)
        ? 1
        : -1;
    },
  },
  category: {
    translation: translations.category,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.examinationReasonDescription) {
        return -1;
      }
      if (!b.examinationReasonDescription) {
        return 1;
      }
      return oldNorseSort(a.examinationReasonDescription, b.examinationReasonDescription);
    },
  },
  registrationDate: {
    translation: translations.registrationDate,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.createDateTime) {
        return -1;
      }
      if (!b.createDateTime) {
        return 1;
      }
      return moment(a.createDateTime).isAfter(b.createDateTime) ? 1 : -1;
    },
  },
  registeredBy: {
    translation: translations.registeredBy,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.requestorName) {
        return -1;
      }
      if (!b.requestorName) {
        return 1;
      }
      return oldNorseSort(a.requestorName, b.requestorName);
    },
  },
  comment: {
    translation: translations.comment,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      if (!a.examinationNotes) {
        return -1;
      }
      if (!b.examinationNotes) {
        return 1;
      }
      return oldNorseSort(a.examinationNotes, b.examinationNotes);
    },
  },
  screeningRequestStatus: {
    translation: translations.status,
    sort: (a: CancerScreeningResultModel, b: CancerScreeningResultModel) => {
      return oldNorseSort(getScreeningStatusText(a.screeningStatus ?? -1, lang), getScreeningStatusText(b.screeningStatus ?? -1, lang));
    },
  },
};

export default ScreeningRequestsOverviewList;
